import { assign, createMachine } from 'xstate';
import { Credentials, CredentialsEntity } from '../../../models/credentials';
import { ApplicationError } from '../../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { credentialsService, CredentialsService } from '../../services/credentialsService';
import { credentialsTimeoutError } from '../utils/utils';

type Context = {
  credentials: CredentialsEntity;
  accounts: Awaited<ReturnType<CredentialsService['getAccounts']>>;
  selectedAccountIds?: string[];
  error?: ApplicationError;
};

export type MachineProps = {
  initialContext: Context;
  onDone: (credentialsChanged: CredentialsEntity) => void;
  service?: CredentialsService;
  initialSate?: string;
  pollInterval?: number;
  pollTimeout?: number;
};

type Services = {
  getAccounts: { data: Awaited<ReturnType<CredentialsService['getAccounts']>> };
  postAccountSelection: { data: void };
  getCredentials: { data: CredentialsEntity };
};

export type Events = { type: 'EVENT_ACCOUNTS_SELECTED'; data: Context['selectedAccountIds'] };

export const createAwaitingAccountsSelectionMachine = ({
  initialContext,
  onDone,
  service = credentialsService,
  initialSate = 'INITIAL_STATE',
  pollInterval = 1000,
  pollTimeout = 60000,
}: MachineProps) =>
  createMachine(
    {
      id: 'AwaitingAccountsSelectionMachine',
      predictableActionArguments: true,
      initial: initialSate,
      context: initialContext,
      tsTypes: {} as import('./awaitingAccountsSelectionMachine.typegen').Typegen0,
      schema: {
        context: {} as Context,
        events: {} as Events,
        services: {} as Services,
      },
      states: {
        INITIAL_STATE: {
          always: { target: 'GET_ACCOUNTS' },
        },
        GET_ACCOUNTS: {
          invoke: {
            src: 'getAccounts',
            onDone: [
              {
                actions: 'set_getAccounts_response',
                target: 'GET_ACCOUNTS_DONE',
              },
            ],
            onError: [
              {
                actions: 'set_error_in_context',
                target: 'ERROR',
              },
            ],
          },
        },
        GET_ACCOUNTS_DONE: {
          on: {
            EVENT_ACCOUNTS_SELECTED: {
              actions: 'set_selectedAccounts',
              target: 'POST_ACCOUNT_SELECTION',
            },
          },
        },
        POST_ACCOUNT_SELECTION: {
          invoke: {
            src: 'postAccountSelection',
            onDone: [
              {
                target: 'POLL_CREDENTIALS',
              },
            ],
            onError: [
              {
                actions: 'set_error_in_context',
                target: 'ERROR',
              },
            ],
          },
        },
        POLL_CREDENTIALS: {
          after: {
            POLL_TIMEOUT: {
              target: '#AwaitingAccountsSelectionMachine.ERROR',
              actions: ['set_polling_timeout_error_in_context'],
              internal: false,
            },
          },
          initial: 'GET_CREDENTIALS',
          states: {
            GET_CREDENTIALS: {
              invoke: {
                src: 'getCredentials',
                onDone: [
                  {
                    target: 'AWAITING',
                    actions: 'update_credentials_in_context',
                  },
                ],
                onError: [
                  {
                    target: '#AwaitingAccountsSelectionMachine.ERROR',
                    actions: 'set_error_in_context',
                  },
                ],
              },
            },
            AWAITING: {
              after: {
                POLL_INTERVAL: {
                  target: '#AwaitingAccountsSelectionMachine.POLL_CREDENTIALS.GET_CREDENTIALS',
                },
              },
            },
          },
          always: [
            {
              target: 'DONE',
              cond: 'check_if_credentials_status_changed',
            },
          ],
        },
        DONE: {
          entry: 'execute_on_done',
          type: 'final',
        },
        ERROR: {
          type: 'final',
        },
      },
    },
    {
      services: {
        getAccounts: ({ credentials }) => service.getAccounts(credentials.id),
        postAccountSelection: ({ credentials, selectedAccountIds = [] }) =>
          service.postAccountSelection(credentials.id, selectedAccountIds),
        getCredentials: ({ credentials }) => service.getCredentials(credentials.id),
      },
      actions: {
        update_credentials_in_context: assign({ credentials: (_, event) => event.data }),
        set_error_in_context: assign({ error: (_, event) => event.data as ApplicationError }),
        set_selectedAccounts: assign({
          selectedAccountIds: (_, event) => event.data,
        }),
        set_getAccounts_response: assign({
          accounts: (_, event) => event.data,
        }),
        execute_on_done: ({ credentials }) => onDone(credentials),
        set_polling_timeout_error_in_context: assign({
          error: (_) => new ApplicationError(credentialsTimeoutError),
        }),
      },
      guards: {
        check_if_credentials_status_changed: ({ credentials }) =>
          !Credentials.isAwaitingAccountsSelection(credentials),
      },
      delays: {
        POLL_INTERVAL: pollInterval,
        POLL_TIMEOUT: pollTimeout,
      },
    }
  );
